import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, LogOut, Trash2, Mail, Phone, RefreshCw, CheckCircle2, Circle,
  Inbox, Loader2, CalendarClock, Briefcase, MessageSquare, Download, FileText, MapPin,
} from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "amenity_admin_token";
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` });

const TABS = [
  { key: "contact", label: "Requests", icon: MessageSquare, endpoint: "/contact" },
  { key: "tours", label: "Tours", icon: CalendarClock, endpoint: "/tours" },
  { key: "careers", label: "Applications", icon: Briefcase, endpoint: "/careers" },
];

function LoginView({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Incorrect password." : "Login failed.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-background px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-3xl border border-black/5 bg-white p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-4 text-center">
          <Logo />
          <div className="grid place-items-center h-12 w-12 rounded-2xl bg-teal/10 text-teal"><Lock size={22} /></div>
          <div>
            <h1 className="font-heading font-semibold text-xl text-ink">Admin sign in</h1>
            <p className="text-sm text-ink/55 mt-1">Enter your password to view submissions.</p>
          </div>
        </div>
        <label htmlFor="admin-pw" className="text-sm font-medium">Password</label>
        <input id="admin-pw" data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-12 w-full rounded-xl border border-input bg-white px-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" placeholder="••••••••" autoFocus />
        <button type="submit" data-testid="admin-login-btn" disabled={loading} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal px-6 py-3 font-medium text-white hover:bg-[#2E6088] transition-colors disabled:opacity-60">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={18} />} Sign in
        </button>
      </form>
    </div>
  );
}

function MetaRow({ item }) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink/60">
      {item.email && <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1.5 hover:text-teal"><Mail size={14} /> {item.email}</a>}
      {item.phone && <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1.5 hover:text-teal"><Phone size={14} /> {item.phone}</a>}
      <span className="text-ink/40">{new Date(item.created_at).toLocaleString()}</span>
    </div>
  );
}

function InboxView({ onLogout }) {
  const [tab, setTab] = useState("contact");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const endpoint = TABS.find((t) => t.key === tab).endpoint;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}${endpoint}`, { headers: authHeader() });
      setItems(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Could not load submissions.");
    } finally {
      setLoading(false);
    }
  }, [endpoint, onLogout]);

  useEffect(() => { load(); }, [load]);

  const toggleRead = async (item) => {
    try {
      const { data } = await axios.patch(`${API}${endpoint}/${item.id}`, { read: !item.read }, { headers: authHeader() });
      setItems((xs) => xs.map((x) => (x.id === item.id ? data : x)));
    } catch { toast.error("Update failed."); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete submission from ${item.name}?`)) return;
    try {
      await axios.delete(`${API}${endpoint}/${item.id}`, { headers: authHeader() });
      setItems((xs) => xs.filter((x) => x.id !== item.id));
      toast.success("Deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const downloadFile = async (f) => {
    try {
      const res = await axios.get(`${API}/files/${f.path}`, { headers: authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.filename || "download"; document.body.appendChild(a); a.click();
      a.remove(); setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("Could not download file."); }
  };

  const unread = items.filter((x) => !x.read).length;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 bg-white/85 backdrop-blur-xl border-b border-black/5">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-[76px] flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh-btn" className="inline-flex items-center gap-2 rounded-full border border-input px-4 py-2 text-sm hover:border-teal/50 transition-colors"><RefreshCw size={15} /> Refresh</button>
            <button onClick={onLogout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-full bg-ink text-white px-4 py-2 text-sm hover:opacity-90 transition-opacity"><LogOut size={15} /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                tab === t.key ? "bg-teal text-white" : "bg-white border border-black/5 text-ink/70 hover:border-teal/40"
              }`}
            >
              <t.icon size={16} /> {t.label}
              {tab === t.key && unread > 0 && <span className="rounded-full bg-white/25 text-white text-xs px-2 py-0.5">{unread}</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid place-items-center py-24 text-ink/50"><Loader2 size={28} className="animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-black/10 bg-white grid place-items-center py-24 text-center">
            <Inbox size={36} className="text-ink/30 mb-3" />
            <p className="font-heading font-semibold text-ink">Nothing here yet</p>
            <p className="text-sm text-ink/55">New submissions will appear in this tab.</p>
          </div>
        ) : (
          <ul data-testid="admin-submissions-list" className="space-y-4">
            {items.map((item) => (
              <li key={item.id} data-testid={`submission-${item.id}`} className={`rounded-3xl border bg-white p-6 transition-colors ${item.read ? "border-black/5" : "border-teal/30 bg-teal/[0.03]"}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-semibold text-lg text-ink">{item.name}</h3>
                      {!item.read && <span className="rounded-full bg-teal text-white text-[11px] px-2 py-0.5">New</span>}
                      {tab === "careers" && item.position && <span className="rounded-full bg-teal/10 text-teal text-xs px-2.5 py-0.5">{item.position}</span>}
                      {tab === "contact" && (item.services_interested || []).map((s) => <span key={s} className="rounded-full bg-teal/10 text-teal text-xs px-2.5 py-0.5">{s}</span>)}
                    </div>
                    <MetaRow item={item} />

                    {tab === "tours" && (
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink/70">
                        <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-teal" /> {item.clinic}</span>
                        <span className="inline-flex items-center gap-1.5"><CalendarClock size={14} className="text-teal" /> {item.preferred_date} · {item.preferred_time}</span>
                        {item.party && <span className="text-ink/50">Party: {item.party}</span>}
                      </div>
                    )}
                    {tab === "contact" && (item.services_interested || []).includes("ABA Therapy") && (
                      <p className="mt-1.5 text-sm text-ink/60">Autism diagnosis (ABA): <span className="font-medium text-ink">{item.autism_diagnosis || "Not provided"}</span></p>
                    )}
                    {tab === "careers" && (item.experience || item.resume_link) && (
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink/70">
                        {item.experience && <span>Experience: {item.experience}</span>}
                        {item.resume_link && <a href={item.resume_link} target="_blank" rel="noopener noreferrer" className="text-teal hover:underline">Resume / LinkedIn</a>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => toggleRead(item)} data-testid={`toggle-read-${item.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-input px-3 py-1.5 text-sm hover:border-teal/50 transition-colors">
                      {item.read ? <Circle size={15} /> : <CheckCircle2 size={15} className="text-teal" />} {item.read ? "Unread" : "Read"}
                    </button>
                    <button onClick={() => remove(item)} data-testid={`delete-${item.id}`} className="grid place-items-center h-9 w-9 rounded-full border border-input text-destructive hover:bg-destructive/10 transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>

                {item.message && <p className="mt-4 rounded-2xl bg-background p-4 text-ink/80 text-[0.95rem] whitespace-pre-wrap">{item.message}</p>}

                {tab === "contact" && (item.attachments || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid={`attachments-${item.id}`}>
                    {item.attachments.map((f) => (
                      <button key={f.id || f.path} onClick={() => downloadFile(f)} className="inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-ink/75 hover:border-teal/50 hover:text-teal transition-colors">
                        <FileText size={14} /> <span className="max-w-[180px] truncate">{f.filename}</span> <Download size={14} />
                      </button>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  useSeo({ title: "Admin | Amenity Health Services", description: "Admin submissions inbox." });
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };
  return authed ? <InboxView onLogout={logout} /> : <LoginView onLogin={() => setAuthed(true)} />;
}
